import { Form } from "antd";
import BaseModalForm from "components/BaseModalForm";
import message from "components/CustomMessage";
import { MODAL_SIZE_MEDIUM } from "constants/Layout";
import { PermissionSetMap } from "constants/RBACConstants";
import _ from "lodash";
import React, { forwardRef, useState } from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { hasPermission } from "utils/authService";
import {
  FieldTypes,
  nameRegexValidator,
  urlRegexValidator,
} from "utils/formUtils";
import { duplicateNameValidator } from "utils/validatorUtil";

import { GenericIntegrationTicketDefaultValues } from "../genericIntegrationTicketConstants";

export type DataDogConfigDataProps = {
  id?: number;
  name?: string;
  url?: string;
  apiToken?: string;
  privateKey?: string;
};

export type DataDogConfigLoginProps = {
  name?: string;
  loginConfigList?: GenericIntegrationTicketDefaultValues[];
  loading?: boolean;
  handleMapping?: (response: any) => void;
  config?: DataDogConfigDataProps;
  onUpdateConfigComponent?: (shouldUpdate?: boolean) => void;
  afterClose?: () => void;
  displayName?: string;
  deleteAPI?: (id: number) => Promise<any>;
  enabledAPI?: (id: number, enabled: boolean) => Promise<any>;
};

function DataDogConfigLoginForm(
  { loginConfigList, handleMapping, config, ...props }: DataDogConfigLoginProps,
  ref: any,
) {
  const isEdit = !_.isUndefined(config);
  const [form] = Form.useForm();
  const [initialValues] = useState<DataDogConfigDataProps>(
    () =>
      config || {
        name: "",
        url: "https://app.datadoghq.com",
        apiToken: "",
        privateKey: "",
      },
  );

  const onOk = async () => {
    await form.validateFields();
    try {
      const payload = form.getFieldsValue();
      const response = await ToolsIntegrationsAPI[
        !isEdit
          ? "postTicketSystemToolLoginDetails"
          : "updateTicketSystemToolLoginDetails"
      ]("DATADOG", payload, config?.id).promise;

      message.success(`DataDog Login Configuration created`);
      if (!isEdit) {
        if (_.isFunction(handleMapping)) {
          handleMapping(response);
        }
      }
      props.onUpdateConfigComponent?.(true);
    } catch (err: any) {
      console.log(err);
    }
  };

  const modalProps = {
    title: (
      <>
        {isEdit ? "Edit" : "Add"} {props.displayName} Login Credentials{" "}
      </>
    ),
    width: MODAL_SIZE_MEDIUM,
    disabled: !hasPermission(
      isEdit ? PermissionSetMap.EDIT_TOOL_CONFIG : PermissionSetMap.WRITE_TOOLS,
    ),
  };

  const fields = [
    {
      name: "name",
      label: "Name",
      colSpan: 12,
      props: {
        autoFocus: true,
      },
      required: true,
      rules: [
        duplicateNameValidator({ list: loginConfigList }),
        nameRegexValidator,
      ],
    },
    {
      name: "url",
      label: "URL",
      colSpan: 12,
      rules: [urlRegexValidator],
      required: true,
    },
    {
      name: "privateKey",
      label: "Application Key",
      type: FieldTypes.PASSWORD,
      colSpan: 12,
      required: true,
    },
    {
      name: "apiToken",
      label: "API Key",
      type: FieldTypes.PASSWORD,
      colSpan: 12,
      required: true,
    },
  ];

  const ModalFormComponent = BaseModalForm;

  return (
    <ModalFormComponent
      modalProps={modalProps}
      ref={ref}
      afterClose={props.afterClose}
      initialValues={initialValues}
      form={form}
      fields={fields}
      onOk={onOk}
      title={modalProps.title}
    />
  );
}

export default forwardRef(DataDogConfigLoginForm);
